import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { ProvisionChange } from '../../models/provisionchange';
import { ProvisionItem } from '../../models/provisionitem';
import { Provision } from '../../models/provision';
import { StructureControlResponse } from '../../models/structurecontrolresponse';
import { StructureControlState } from '../../models/structurecontrolstate';
import { Suggestion } from '../../models/suggestion';
import { StructureService } from '../../services/structure/structure.service';
import { ErrorService } from '../../services/error/error.service';
import { StateService } from '../../services/state/state.service';
import { ProvisionService } from '../../services/provision/provision.service';
import { SpecItemDescriptionService } from '../../services/specitemdescription/specitemdescription.service';

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
    selector: 'conversion-changes',
    templateUrl: './changes.component.html',
    styleUrls: ['./changes.component.css']
})
export class ChangesComponent implements OnInit, OnDestroy {
    @Input() id: string;
    changes: ProvisionChange[] = [];
    structurecontrolresponse: StructureControlResponse;
    tcrvcrRunning = false;
    vspeccollapsed = true;
    fetchingDescriptions = false;
    validateOnChange = false;
    provision: Provision;
    lockedFPCs: string;
    analysisFailed = false;
    analysisStale = false;
    private subscriptions: Subscription[] = [];
    private activeAnalysis: Subscription;
    private activeDescriptions: Subscription;
    private provisionBusy = false;

    constructor(private specitemdescriptionService: SpecItemDescriptionService,
                public provisionService: ProvisionService,
                private structureService: StructureService,
                private errorService: ErrorService,
                private stateService: StateService) {}

    get canAnalyze(): boolean {
        return this.provisionService.hasValidAnalysisInputs() && !this.tcrvcrRunning && !this.provisionBusy;
    }

    get analysisVariant(): 'information' | 'warning' | 'success' | 'error' {
        if (this.tcrvcrRunning) { return 'information'; }
        if (this.analysisFailed) { return 'error'; }
        if (this.analysisStale) { return 'information'; }
        if (this.structurecontrolresponse) { return this.structurecontrolresponse.Success ? 'success' : 'warning'; }
        return 'information';
    }

    get analysisTitle(): string {
        if (this.tcrvcrRunning) { return 'Analyzing changes'; }
        if (this.analysisFailed) { return 'Analysis could not be completed'; }
        if (this.analysisStale) { return 'Analysis required'; }
        if (this.structurecontrolresponse) { return this.structurecontrolresponse.Success ? 'Analysis successful' : 'Changes need attention'; }
        return 'Not analyzed';
    }

    get analysisDescription(): string {
        if (this.tcrvcrRunning) { return 'Checking the configuration.'; }
        if (this.analysisFailed) { return 'Your edits are preserved. Try Analyze again.'; }
        if (this.analysisStale) { return 'The configuration or analysis settings have changed. Analyze again before saving.'; }
        if (this.structurecontrolresponse) { return this.structurecontrolresponse.Success ? 'The analysis completed successfully.' : 'Review the suggestions and messages below.'; }
        if (this.provision?.Name && !this.provisionService.hasValidAnalysisInputs()) { return 'Enter a valid part period and start object before analyzing.'; }
        return this.provision?.Name ? 'Use Analyze to check the configuration.' : 'Load a provision to start working.';
    }

    ngOnInit(): void {
        this.subscriptions.push(this.provisionService.getProvisionUpdates().subscribe(provision => {
            if (this.provision !== provision) { this.analysisFailed = false; }
            this.provision = provision;
        }));
        this.subscriptions.push(this.provisionService.getStructureControlResponseUpdates().subscribe(response => {
            this.structurecontrolresponse = response;
            if (!response) { this.analysisStale = false; }
        }));
        this.subscriptions.push(this.structureService.getLockedFPCs().subscribe(
            items => this.lockedFPCs = items.map(item => item.Family).join(','),
            error => this.errorService.insertError('The analysis settings could not be loaded. ' + error)
        ));
        this.subscriptions.push(this.stateService.getTcrVcrRunningUpdates().subscribe(running => this.tcrvcrRunning = running));
        this.subscriptions.push(this.stateService.getProvisionBusyUpdates().subscribe(busy => this.provisionBusy = busy));
        this.subscriptions.push(this.provisionService.getprovisionchangesUpdates().subscribe(changes => {
            if (changes !== null) {
                this.changes = changes;
            }
        }));
        this.subscriptions.push(this.provisionService.getAnalysisInputUpdates().subscribe(reason => {
            this.cancelAnalysis();
            this.analysisFailed = false;
            this.analysisStale = !!this.structurecontrolresponse;
            if (reason === 'edit' && this.validateOnChange && this.canAnalyze) { this.validateProvision(); }
        }));
    }

    removeChange(item: ProvisionChange): void { this.provisionService.removeChange(item); }
    removeSuggestion(suggestion: Suggestion): void {
        const response = this.structurecontrolresponse;
        if (!response?.Suggestions?.includes(suggestion)) { return; }
        this.provisionService.insertStructureControlResponse({
            ...response, Suggestions: response.Suggestions.filter(item => item !== suggestion)
        });
    }
    trackChange(_index: number, item: ProvisionChange): string { return `${item.Type}:${item.Family}`; }

    selectExecution(current: ProvisionChange, execution: ProvisionItem): void {
        this.provisionService.insertChange([new ProvisionChange(current.Type, current.Family, current.FamilyDescription,
            current.FromExecution, current.FromExecutionDescription, execution.Execution, execution.ExecutionDescription)]);
    }

    validateProvision(): void {
        if (!this.canAnalyze) { return; }
        this.cancelAnalysis();
        this.analysisFailed = false;
        this.errorService.insertError(null);
        this.stateService.insertTcrVcrRunning(true);
        this.provisionService.insertStructureControlState(new StructureControlState(true, false));
        this.activeAnalysis = this.structureService.validateProvision().pipe(
            take(1), finalize(() => this.stateService.insertTcrVcrRunning(false))
        ).subscribe({
            next: response => {
                this.analysisStale = false;
                this.provisionService.insertStructureControlResponse(response);
                this.provisionService.insertStructureControlState(new StructureControlState(false, response.Success));
                response.VSpec.sort((a, b) => parseInt(a.Family) - parseInt(b.Family));
                this.fetchingDescriptions = true;
                this.activeDescriptions = this.specitemdescriptionService.getProvisionItemFamilyAndExecutionDescription(response.VSpec.filter(item => item.Type === 'FPC'))
                    .pipe(finalize(() => this.fetchingDescriptions = false))
                    .subscribe({
                        next: descriptions => {
                            if (descriptions) {
                                response.VSpec.filter(item => item.Type === 'FPC').forEach(item => {
                                    const description = descriptions[item.Family];
                                    if (description) {
                                        item.FamilyDescription = description.FamilyDescription;
                                        item.ExecutionDescription = description.ExecutionDescription;
                                    }
                                });
                            }
                        },
                        error: error => this.errorService.insertError('The analysis completed, but descriptions could not be loaded. ' + error)
                    });
            },
            error: error => {
                this.analysisFailed = true;
                this.errorService.insertError(error);
                this.provisionService.insertStructureControlState(new StructureControlState(false, false));
            }
        });
    }

    private cancelAnalysis(): void {
        this.activeAnalysis?.unsubscribe();
        this.activeDescriptions?.unsubscribe();
        this.fetchingDescriptions = false;
    }

    ngOnDestroy(): void {
        this.cancelAnalysis();
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
